import { setDepartureLine } from './multipleBirth';

/**
 * What a Delete Person actually takes with it.
 *
 * The confirmation dialog has always named the person and made you type their UPN
 * to proceed. What it never said is what happens to everyone attached to them, and
 * the answer is more surprising than "nothing": **nothing cascades**. Children are
 * not deleted, they are ORPHANED — `fatherId`/`motherId` go null and they drop out
 * of `childrenIds`, both client-side (`stripMemberReferencesToPid`) and on the
 * server. On a three-generation pedigree that leaves a parentless sibship floating
 * mid-chart, with no warning, from a menu item that reads like it removes one
 * record.
 *
 * This module computes that list so the dialog can state it. TGCLOUD-1644.
 *
 * WHAT EARNS A LINE, and what does not.
 *
 * A warning that fires on every deletion stops being read. Browser QA found the
 * first version causing hesitation on routine deletions, so the bar is now
 * explicit: **a consequence is listed only when it could leave the pedigree in a
 * state the clinician would not have chosen** — a section orphaned, a union split,
 * or clinical information lost that cannot be recovered from what remains.
 *
 * By that bar:
 * - **Orphaned children** — listed. This is the whole reason the block exists.
 * - **Unions** — listed. Removing one partner can split a pedigree in two.
 * - **Twin links** — listed. The survivor stops being drawn as a twin, and
 *   zygosity is genetic information that cannot be re-derived from the chart.
 * - **Sibling links** — NOT listed. Siblinghood between the survivors is
 *   unchanged: they still share the parents they shared before, and the drawing
 *   does not move. Nothing is lost, so saying so was pure noise.
 * - **Care pathways** — listed ONLY when some exist. See below.
 */

/**
 * The care-pathway line, which needs a COUNT rather than a hunch.
 *
 * The server deletes every `PathwayInstance` and `PathwayEvent` for the member,
 * and nothing about them is loaded with the pedigree — so the first version said
 * so unconditionally, without a number (decision D1). Browser QA reversed that:
 * on the great majority of deletions there are no pathway records at all, and an
 * always-on warning about records that do not exist is exactly the undue
 * hesitation the block was meant to avoid. `GET /pedigree/pathway-count/:id`
 * exists so the dialog can ask, and stay quiet when the answer is zero.
 */
export const pathwayNote = (count) =>
  count === 1
    ? 'Their 1 care-pathway record will also be deleted.'
    : `Their ${count} care-pathway records will also be deleted.`;

/**
 * Fallback wording for when the count could not be fetched. Deliberately still
 * shown: failing to warn about a real deletion is worse than a moment's pause,
 * and this path is rare.
 */
export const PATHWAY_NOTE_UNKNOWN =
  'Any care-pathway records for this person will also be deleted.';

/** The reassurance that stops the orphan lines reading as "the children go too". */
export const CHILDREN_KEPT_NOTE =
  'Their children stay on the pedigree and are not deleted.';

const displayName = (m) =>
  [m?.firstName, m?.lastName].filter(Boolean).join(' ') ||
  (m?.upn != null ? `UPN ${m.upn}` : `Person ${m?.pid}`);

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * @param {object} node    the person about to be deleted
 * @param {Array}  members the member records the chart is drawn from
 * @returns {{ lines: string[], childCount: number, spouseNames: string[],
 *            twinCount: number }}
 *
 * `lines` is ready to render, already filtered of anything that would read "0".
 * A dialog that always shows five bullets, three of them zero, teaches people to
 * skip it — so a leaf node gets no bullets at all beyond the pathway note.
 */
export const deleteConsequences = (node, members) => {
  const pid = node?.pid;
  const list = Array.isArray(members) ? members : [];

  const empty = {
    lines: [],
    childCount: 0,
    spouseNames: [],
    twinCount: 0
  };
  if (pid == null) return empty;

  // Children are found from the CHILD's own back-pointers rather than the
  // parent's childrenIds: the back-pointers are what the delete actually nulls,
  // and a childrenIds array can carry a stale pid (see analyzeMemberEdges).
  const children = list.filter(
    (m) => m && (m.fatherId === pid || m.motherId === pid)
  );

  // A child who would be left with NO parent at all is the worse case, and worth
  // separating: losing one of two parents leaves the descent line drawn.
  const fullyOrphaned = children.filter((c) => {
    const other = c.fatherId === pid ? c.motherId : c.fatherId;
    return other == null || !list.some((m) => m.pid === other);
  });

  const spouseNames = (
    list.find((m) => m.pid === pid)?.spouseIds ||
    node?.spouseIds ||
    []
  )
    .map((sid) => list.find((m) => m.pid === sid))
    .filter(Boolean)
    .map(displayName);

  const self = list.find((m) => m.pid === pid) || node || {};
  // NOTE: siblingIds is deliberately NOT counted — see the header. The survivors
  // remain each other's siblings through the parents they already share.
  const twinCount = (self.twinRelations || []).filter((r) =>
    list.some((m) => m.pid === r?.withPid)
  ).length;

  const lines = [];

  if (children.length) {
    const parentRole = self.gender === 'female' ? 'mother' : 'father';
    lines.push(
      fullyOrphaned.length === children.length
        ? `leave ${plural(
            children.length,
            'child',
            'children'
          )} with no recorded parent`
        : `leave ${plural(
            children.length,
            'child',
            'children'
          )} without a recorded ${parentRole}`
    );
  }

  if (spouseNames.length) {
    lines.push(
      spouseNames.length === 1
        ? `remove their union with ${spouseNames[0]}`
        : `remove ${plural(
            spouseNames.length,
            'union',
            'unions'
          )} — ${spouseNames.join(', ')}`
    );
  }

  if (twinCount) {
    // Above a pair the LINK COUNT is not the consequence — what survives is. Deleting
    // one of three co-multiples leaves two twins, not three singletons, and the dialog
    // should say so (TGCLOUD-1692). A pair keeps the existing wording: there is no
    // remainder to describe, and the count is not misleading at that size.
    lines.push(
      setDepartureLine(twinCount + 1) ||
        `remove ${plural(twinCount, 'twin link', 'twin links')}`
    );
  }

  return {
    lines,
    childCount: children.length,
    spouseNames,
    twinCount
  };
};
